import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Chip,
    Collapse,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import { toPriceWithComma } from "../../../helper/utils";
import { CreditStatusEnum } from "../../../helper/enums/CreditStatusEnum";
import {
    BlockRounded,
    CalendarTodayRounded,
    EventAvailableRounded,
    ExpandLessRounded,
    ExpandMoreRounded,
    RemoveCircleOutlineRounded,
} from "@mui/icons-material";
import { TransactionPersonnelCredit_query } from "../../../network/api/transactionPersonnelCredit.api";
import { ErrorContext } from "../../../components/GympinPagesProvider";
import { corporatePersonnel_manualExpireCredit } from "../../../network/api/corporatePersonnel.api";
import { CreditUsageCategoryEnum } from "../../../helper/enums/CreditUsageCategoryEnum";
import _CreditExpireModal from "./_CreditExpireModal";
import _CreditTransactionsList from "./_CreditTransactionsList";

const _UserCreditListItem = ({ userCredit, updatePage }) => {
    const error = useContext(ErrorContext);
    const [creditToDecrease, setCreditToDecrease] = useState(null);
    const [openDetails, setOpenDetails] = useState(false);
    const [creditTransactions, setCreditTransaction] = useState([]);
    const [loadingTransactions, setLoadingTransactions] = useState(false);
    const [submittingExpire, setSubmittingExpire] = useState(false);

    useEffect(() => {
        if (!openDetails) return;
        getTransactionDetails();
    }, [openDetails]);

    function getTransactionDetails() {
        setLoadingTransactions(true);
        TransactionPersonnelCredit_query({
            queryType: "FILTER",
            CreditId: userCredit.Id,
            paging: { Page: 0, Size: 100, Desc: true },
        })
            .then((data) => {
                setCreditTransaction(data.data.Data?.content || []);
                setLoadingTransactions(false);
            })
            .catch(e => {
                setLoadingTransactions(false);
                const msg = e?.response?.data?.Message || "خطا در دریافت تاریخچه تراکنش‌ها";
                error.showError({ message: msg });
            });
    }

    function creditExpire(e) {
        e.preventDefault();
        setSubmittingExpire(true);
        corporatePersonnel_manualExpireCredit({
            Id: userCredit.Id,
        })
            .then(() => {
                setSubmittingExpire(false);
                setCreditToDecrease(null);
                error.showError({ message: "اعتبار با موفقیت منقضی شد" });
                if (updatePage) updatePage();
            })
            .catch(e => {
                setSubmittingExpire(false);
                const msg = e?.response?.data?.Message || "خطا در منقضی‌سازی اعتبار";
                error.showError({ message: msg });
            });
    }

    const isActive = userCredit?.Status === "ACTIVE";
    const statusLabel = isActive ? "فعال" : (CreditStatusEnum[userCredit?.Status] || userCredit?.Status || "نامشخص");

    // Specification: اگر UsageCategories خالی بود → فرض شود ["SPORT"]
    const categories = (userCredit?.UsageCategories && userCredit.UsageCategories.length > 0)
        ? userCredit.UsageCategories
        : ["SPORT"];

    const blockedSports = userCredit?.BlockedSports || [];
    const hasBlockedSports = blockedSports.length > 0;

    return (
        <Box
            sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: openDetails ? 'primary.main' : 'divider',
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                transition: 'all 0.2s ease',
                overflow: 'hidden',
            }}
        >
            {/* Header Row */}
            <Box
                onClick={() => setOpenDetails(!openDetails)}
                sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    '&:hover': {
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(45, 107, 226, 0.03)',
                    },
                }}
            >
                {/* Credit Title & Details */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2.5,
                            bgcolor: isActive ? 'rgba(16, 185, 129, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                            color: isActive ? 'success.main' : 'text.disabled',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 800,
                            flexShrink: 0,
                        }}
                    >
                        <EventAvailableRounded sx={{ fontSize: 22 }} />
                    </Box>

                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.4 }}>
                            <Typography variant="body1" sx={{ fontWeight: 800, color: 'text.primary' }}>
                                {toPriceWithComma(userCredit.CreditAmount)} تومان
                            </Typography>
                            {userCredit.Name && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                    ({userCredit.Name})
                                </Typography>
                            )}
                            <Chip
                                size="small"
                                label={statusLabel}
                                sx={{
                                    height: 22,
                                    fontSize: '0.72rem',
                                    fontWeight: 700,
                                    borderRadius: 1.5,
                                    bgcolor: isActive ? 'rgba(16, 185, 129, 0.1)' : 'action.hover',
                                    color: isActive ? 'success.main' : 'text.disabled',
                                }}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 0.6 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                <CalendarTodayRounded sx={{ fontSize: 13 }} />
                                <Typography variant="caption">
                                    ایجاد: {new Date(userCredit?.CreatedDate).toLocaleDateString('fa-IR', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                    })}
                                </Typography>
                            </Box>

                            {userCredit?.ExpireDate && (
                                <Typography variant="caption" sx={{ color: isActive ? 'text.secondary' : 'text.disabled' }}>
                                    انقضا: {new Date(userCredit?.ExpireDate).toLocaleDateString('fa-IR', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                    })}
                                </Typography>
                            )}
                        </Box>

                        {/* بخش‌های مصرف و محدودیت ورزش‌ها */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                            {categories.map((code) => (
                                <Chip
                                    key={code}
                                    size="small"
                                    label={CreditUsageCategoryEnum[code]?.label || code}
                                    sx={{
                                        height: 20,
                                        fontSize: '0.68rem',
                                        fontWeight: 700,
                                        bgcolor: 'rgba(45, 107, 226, 0.08)',
                                        color: 'primary.main',
                                        border: '1px solid',
                                        borderColor: 'rgba(45, 107, 226, 0.2)',
                                    }}
                                />
                            ))}

                            {hasBlockedSports ? (
                                <Chip
                                    size="small"
                                    icon={<BlockRounded sx={{ fontSize: '11px !important' }} />}
                                    color="error"
                                    variant="outlined"
                                    label={`ممنوع: ${blockedSports.map(s => s.Name).join('، ')}`}
                                    sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
                                />
                            ) : (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label="همه ورزش‌ها مجاز"
                                    sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, color: 'text.secondary' }}
                                />
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Actions & Expand Icon */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isActive && (userCredit?.CreditAmount || 0) > 0 && (
                        <Tooltip title="منقضی‌سازی دستی این اعتبار">
                            <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCreditToDecrease(userCredit);
                                }}
                                sx={{
                                    bgcolor: 'rgba(239, 68, 68, 0.08)',
                                    '&:hover': { bgcolor: 'error.main', color: 'white' },
                                }}
                            >
                                <RemoveCircleOutlineRounded fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}

                    <IconButton size="small" sx={{ color: 'text.secondary' }}>
                        {openDetails ? <ExpandLessRounded /> : <ExpandMoreRounded />}
                    </IconButton>
                </Box>
            </Box>

            {/* Collapsible Transactions History */}
            <Collapse in={openDetails} timeout="auto" unmountOnExit>
                <_CreditTransactionsList
                    loading={loadingTransactions}
                    transactions={creditTransactions}
                />
            </Collapse>

            {/* Expire Dialog Modal */}
            <_CreditExpireModal
                open={!!creditToDecrease}
                onClose={() => setCreditToDecrease(null)}
                onSubmit={creditExpire}
                submitting={submittingExpire}
                userCredit={userCredit}
            />
        </Box>
    );
};

export default _UserCreditListItem;
